import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SHARED_TRANSFER_CITY_PAIRS } from '../config/sharedTransferLocations';
import SharedTripRouteIllustration from './SharedTripRouteIllustration';

const STEPS = [
  { label: 'Choose your journey' },
  { label: 'Others can join' },
  { label: 'Travel together' },
];

const pairKey = (pickup, dropoff) => `${pickup}-${dropoff}`;

const SharedTripsEmptyState = ({ compact = false }) => {
  const [selected, setSelected] = useState(null);
  const heading = compact
    ? 'No shared trips yet — start one'
    : 'Be the first traveller';
  const body = compact
    ? 'Travelling between Nairobi, Arusha or Moshi? Start a trip for the journey you need and we\u2019ll open it for other travellers to join.'
    : 'No shared trips match this week yet. Start the journey you need and we\u2019ll make it available for other passengers to join.';

  const ctaHref = selected
    ? `/create?pickup=${selected.pickup}&dropoff=${selected.dropoff}`
    : '/create';
  const ctaLabel = selected
    ? `Start ${selected.pickup} → ${selected.dropoff} trip`
    : 'Start a shared trip';

  return (
    <div className="py-10 md:py-12">
      <SharedTripRouteIllustration compact={compact} />

      <div className="text-center mt-6 max-w-xl mx-auto">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-[#2D1E17] mb-2">
          {heading}
        </h3>
        <p className="text-stone-500 text-sm md:text-base leading-relaxed">{body}</p>

        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-wider text-[#7A6E67] mb-3">
            Where are you going?
          </p>
          <div className="flex flex-wrap justify-center gap-2" role="group" aria-label="Route options">
            {SHARED_TRANSFER_CITY_PAIRS.map(({ pickup, dropoff }) => {
              const isSelected = selected
                && selected.pickup === pickup
                && selected.dropoff === dropoff;
              return (
                <button
                  key={pairKey(pickup, dropoff)}
                  type="button"
                  aria-pressed={isSelected ? 'true' : 'false'}
                  onClick={() => setSelected(
                    isSelected ? null : { pickup, dropoff },
                  )}
                  className={`inline-flex items-center px-3.5 py-1.5 rounded-full border text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A44432] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF3E0] ${
                    isSelected
                      ? 'border-[#A44432] bg-[#A44432]/10 font-semibold text-[#2D1E17] ring-1 ring-[#A44432]'
                      : 'border-[#E8DED1] bg-white font-medium text-[#2D1E17] hover:border-[#A44432] hover:text-[#A44432]'
                  }`}
                >
                  {pickup} → {dropoff}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <Link
            to={ctaHref}
            className="btn-primary inline-flex items-center justify-center"
          >
            {ctaLabel}
          </Link>
        </div>

        <ol
          className="mt-8 flex flex-wrap justify-center items-center gap-x-2 gap-y-1 text-xs text-stone-400"
          aria-label="How shared trips work"
        >
          {STEPS.map((step, index) => (
            <li key={step.label} className="inline-flex items-center gap-2">
              <span className="font-medium text-stone-500">{step.label}</span>
              {index < STEPS.length - 1 && (
                <span aria-hidden="true" className="text-[#D8CBB6]">→</span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default SharedTripsEmptyState;
